import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, MessageCircle, Map as MapIcon,
  CheckCircle2, ExternalLink, FileText,
  Github, Layers, ArrowRight
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const capabilities = [
    { title: 'Agent Discovery', desc: 'Global index for finding and connecting with AI agents' },
    { title: 'Verified Identity', desc: 'Cryptographic AgentFacts for trust and authentication' },
    { title: 'Multi-Protocol', desc: 'A2A, MCP, and emerging protocol support' },
    { title: 'Real-time Telemetry', desc: 'Live monitoring across the entire agent network' },
    { title: 'Framework Agnostic', desc: 'LangGraph, CrewAI, custom frameworks, all supported' },
    { title: 'Open Infrastructure', desc: 'Build anything on top of NEST. No gatekeepers' }
  ];

  const howItWorks = [
    { title: 'Build', description: 'Create agents, protocols, security tools, or coordination systems. NEST provides the infrastructure layer. You provide the innovation.' },
    { title: 'Connect', description: 'Register on the NANDA Index. Your project becomes discoverable, authenticated, and interoperable with every other project in the ecosystem.' },
    { title: 'Scale', description: 'Leverage the full NEST network. Thousands of agents, global distribution, real-time communication, all without building infrastructure from scratch.' }
  ];

  return (
    <div style={{ background: '#000', minHeight: '100vh' }}>

      {/* ===== HERO ===== */}
      <div style={{
        minHeight: '85vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '80px 20px 60px' : '120px 64px 80px'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ marginBottom: '32px' }}>
            <img src="/nanda-logo-gold-white.svg" alt="Project NANDA" style={{ height: '60px', objectFit: 'contain' }} />
          </div>

          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            fontWeight: '600',
            color: '#fff',
            letterSpacing: '-0.03em',
            lineHeight: '1.15',
            marginBottom: '24px'
          }}>
            <span style={{ color: '#7a2020' }}>NANDA Exchange</span> Sandbox{isMobile ? ' ' : <br />}and TestNet
          </h1>

          <p style={{
            fontSize: 'clamp(0.95rem, 1.3vw, 1.05rem)',
            color: '#666',
            maxWidth: '600px',
            margin: '0 auto 40px',
            lineHeight: '1.7'
          }}>
            Build, Test, Launch and Connect with Agentic Projects Worldwide.
            The foundational layer where autonomous AI systems discover, communicate, and coordinate.
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/ecosystem')}
              style={{
                padding: '12px 28px',
                background: '#7a2020',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Layers size={16} />
              Explore Ecosystem
            </button>
            <button
              onClick={() => navigate('/agents')}
              style={{
                padding: '12px 28px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Users size={16} />
              Browse Agents
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 64px' }}>

        {/* ===== ABOUT NEST ===== */}
        <div style={{ marginBottom: isMobile ? '80px' : '120px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 14px',
              border: '1px solid rgba(122,32,32,0.3)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: '#7a2020',
              fontWeight: '600',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '20px'
            }}>
              About NEST
            </span>
            <h2 style={{
              fontSize: 'clamp(1.4rem, 3vw, 2rem)',
              fontWeight: '600',
              color: '#fff',
              letterSpacing: '-0.02em',
              marginBottom: '12px'
            }}>
              The Foundation of the Agentic Web
            </h2>
            <p style={{ color: '#666', fontSize: '0.9rem', maxWidth: '600px', margin: '0 auto', lineHeight: '1.7' }}>
              NEST is the open infrastructure that everything in the agentic web runs on.
              Think of it like the internet backbone, but for autonomous AI agents.
            </p>
          </div>

          <div style={{
            background: '#0a0a0a',
            borderRadius: '8px',
            padding: isMobile ? '28px 20px' : '40px 48px',
            border: '1px solid rgba(255,255,255,0.06)',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: '0.95rem',
              lineHeight: '1.8',
              color: '#999',
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              <strong style={{ color: '#7a2020', fontWeight: '600' }}>NEST</strong> provides agent discovery, identity verification, real-time communication,
              and coordination primitives that any project can build on top of. Security tools, payment systems,
              coordination protocols, communication layers. They all share the same infrastructure.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '12px'
          }}>
            {capabilities.map((cap, i) => (
              <div key={i} style={{
                padding: '24px',
                background: '#0a0a0a',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.06)'
              }}>
                <CheckCircle2 size={18} style={{ color: '#7a2020', marginBottom: '12px' }} />
                <p style={{ fontWeight: '500', fontSize: '0.9rem', color: '#fff', marginBottom: '6px' }}>
                  {cap.title}
                </p>
                <p style={{ fontSize: '0.8rem', color: '#666', lineHeight: '1.6' }}>
                  {cap.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ===== LIVE NETWORK ===== */}
        <div style={{ marginBottom: isMobile ? '80px' : '120px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 14px',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: '#666',
              fontWeight: '600',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '20px'
            }}>
              Live Network
            </span>
            <h2 style={{
              fontSize: 'clamp(1.4rem, 3vw, 2rem)',
              fontWeight: '600',
              color: '#fff',
              letterSpacing: '-0.02em'
            }}>
              See the Network in Action
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '12px'
          }}>
            {[
              { icon: Users, title: 'Agent Registry', description: '2,300+ agents registered, discoverable, and interoperating across the network.', stats: '2,300+ agents', path: '/agents' },
              { icon: MessageCircle, title: 'Live Messages', description: 'Real-time agent-to-agent communications flowing across NEST infrastructure.', stats: 'Real-time', path: '/messages' },
              { icon: MapIcon, title: 'Global Network', description: 'Agents distributed across 10+ regions worldwide. Interactive topology.', stats: '10+ regions', path: '/map' }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  onClick={() => navigate(item.path)}
                  style={{
                    background: '#0a0a0a',
                    borderRadius: '8px',
                    padding: '32px 24px',
                    border: '1px solid rgba(255,255,255,0.06)',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(122,32,32,0.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}
                >
                  <Icon size={20} style={{ color: '#7a2020', marginBottom: '16px' }} />
                  <h4 style={{ fontSize: '1rem', fontWeight: '500', color: '#fff', marginBottom: '8px' }}>
                    {item.title}
                  </h4>
                  <p style={{ fontSize: '0.8rem', color: '#666', lineHeight: '1.6', marginBottom: '16px' }}>
                    {item.description}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.75rem', color: '#7a2020', fontWeight: '500' }}>{item.stats}</span>
                    <ArrowRight size={14} style={{ color: '#333' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== HOW IT WORKS ===== */}
        <div style={{ marginBottom: isMobile ? '80px' : '120px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 14px',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: '#666',
              fontWeight: '600',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '20px'
            }}>
              How It Works
            </span>
            <h2 style={{
              fontSize: 'clamp(1.4rem, 3vw, 2rem)',
              fontWeight: '600',
              color: '#fff',
              letterSpacing: '-0.02em'
            }}>
              Build on NEST in Three Steps
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '12px'
          }}>
            {howItWorks.map((step, i) => (
              <div key={i} style={{
                background: '#0a0a0a',
                borderRadius: '8px',
                padding: '32px 24px',
                border: '1px solid rgba(255,255,255,0.06)',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '16px', right: '20px',
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  color: 'rgba(122,32,32,0.12)',
                  lineHeight: '1'
                }}>
                  {i + 1}
                </div>
                <h4 style={{ fontSize: '1rem', fontWeight: '500', color: '#fff', marginBottom: '10px' }}>
                  {step.title}
                </h4>
                <p style={{ fontSize: '0.8rem', color: '#666', lineHeight: '1.7' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ===== STATS ===== */}
        <div style={{ marginBottom: isMobile ? '80px' : '120px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: '600', color: '#fff', letterSpacing: '-0.02em' }}>
              The NEST Network
            </h2>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: '12px'
          }}>
            {[
              { value: '6,000+', label: 'Network Participants' },
              { value: '75+', label: 'Projects on NEST' },
              { value: '61', label: 'Organization Partners' },
              { value: '18', label: 'Global Regions' },
              { value: '2,300+', label: 'Registered Agents' },
              { value: '15+', label: 'Research Papers' },
              { value: '12', label: 'Hackathons' },
              { value: '100+', label: 'Industry Speakers' }
            ].map((stat, idx) => (
              <div key={idx} style={{
                background: '#0a0a0a',
                borderRadius: '8px',
                padding: '24px 16px',
                border: '1px solid rgba(255,255,255,0.06)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#7a2020', marginBottom: '6px' }}>
                  {stat.value}
                </div>
                <p style={{ fontSize: '0.75rem', color: '#555' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ===== RESEARCH ===== */}
        <div style={{ marginBottom: isMobile ? '80px' : '120px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 14px',
              border: '1px solid rgba(122,32,32,0.3)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: '#7a2020',
              fontWeight: '600',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '20px'
            }}>
              Research
            </span>
            <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: '600', color: '#fff', letterSpacing: '-0.02em' }}>
              Academic Foundations
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '12px'
          }}>
            {[
              {
                title: 'Beyond DNS: Unlocking the Internet of AI Agents',
                subtitle: 'NANDA Index, Verified AgentFacts and Adaptive Resolution',
                description: 'Architecture for discoverability and authentication in the internet of AI agents.',
                url: 'https://arxiv.org/abs/2507.14263'
              },
              {
                title: 'Upgrade or Switch?',
                subtitle: 'Next-Gen Trusted Architecture for the Internet of AI Agents',
                description: 'Analysis of upgrading existing vs implementing purpose-built agent index architectures.',
                url: 'https://arxiv.org/abs/2506.12003'
              }
            ].map((paper, i) => (
              <div key={i} style={{
                background: '#0a0a0a',
                borderRadius: '8px',
                padding: '28px',
                border: '1px solid rgba(255,255,255,0.06)',
                transition: 'border-color 0.2s'
              }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(122,32,32,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' }}>
                  <FileText size={20} style={{ color: '#7a2020', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: '500', color: '#fff', marginBottom: '4px', lineHeight: '1.4' }}>
                      {paper.title}
                    </h4>
                    <p style={{ fontSize: '0.75rem', color: '#555' }}>{paper.subtitle}</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.8rem', color: '#666', lineHeight: '1.6', marginBottom: '16px' }}>
                  {paper.description}
                </p>
                <a
                  href={paper.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '8px 14px',
                    border: '1px solid rgba(122,32,32,0.3)',
                    borderRadius: '4px',
                    color: '#7a2020',
                    textDecoration: 'none',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                  }}
                >
                  Read on arXiv <ExternalLink size={12} />
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* ===== CTA ===== */}
        <div style={{
          marginBottom: isMobile ? '80px' : '100px',
          background: '#0a0a0a',
          border: '1px solid rgba(122,32,32,0.2)',
          borderRadius: '8px',
          padding: isMobile ? '40px 24px' : '56px 48px',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: '600', color: '#fff', marginBottom: '12px' }}>
            Build on NEST
          </h2>
          <p style={{ fontSize: '0.9rem', color: '#666', maxWidth: '500px', margin: '0 auto 28px', lineHeight: '1.6' }}>
            Whether you're building the next security protocol or a new coordination framework,
            NEST gives you the infrastructure to make it real.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/ecosystem')}
              style={{
                padding: '12px 28px',
                background: '#7a2020',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Submit Your Project <ArrowRight size={16} />
            </button>
            <a
              href="https://github.com/projnanda/NEST"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '12px 28px',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '0.875rem',
                fontWeight: '500',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Github size={16} /> GitHub <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>

      {/* ===== FOOTER ===== */}
      <footer style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: isMobile ? '40px 20px 24px' : '48px 64px 24px',
        background: '#000'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: isMobile ? '32px' : '40px',
            marginBottom: '32px'
          }}>
            <div>
              <img src="/nanda-logo-gold-white.svg" alt="Project NANDA" style={{ height: '32px', marginBottom: '16px' }} />
              <p style={{ fontSize: '0.8rem', color: '#555', lineHeight: '1.6' }}>
                The infrastructure layer for the agentic web. Built by Project NANDA.
              </p>
            </div>
            <div>
              <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#666', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Platform</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  { label: 'Ecosystem', href: '/ecosystem' },
                  { label: 'Agents', href: '/agents' },
                  { label: 'Messages', href: '/messages' },
                  { label: 'Global Map', href: '/map' }
                ].map((link, i) => (
                  <li key={i} style={{ marginBottom: '10px' }}>
                    <a href={link.href} style={{ fontSize: '0.8rem', color: '#555', textDecoration: 'none' }}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#666', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Resources</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  { label: 'GitHub', href: 'https://github.com/projnanda/NEST' },
                  { label: 'Research Papers', href: 'https://arxiv.org/abs/2507.14263' }
                ].map((link, i) => (
                  <li key={i} style={{ marginBottom: '10px' }}>
                    <a href={link.href} target="_blank" rel="noopener noreferrer" style={{
                      fontSize: '0.8rem', color: '#555', textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: '4px'
                    }}>
                      {link.label} <ExternalLink size={10} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#666', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Project NANDA</h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  { label: 'Website', href: 'https://projectnanda.org' },
                  { label: 'Events', href: 'https://luma.com/nanda' }
                ].map((link, i) => (
                  <li key={i} style={{ marginBottom: '10px' }}>
                    <a href={link.href} target="_blank" rel="noopener noreferrer" style={{
                      fontSize: '0.8rem', color: '#555', textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: '4px'
                    }}>
                      {link.label} <ExternalLink size={10} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{
            paddingTop: '24px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '8px'
          }}>
            <p style={{ fontSize: '0.75rem', color: '#444', margin: 0 }}>
              &copy; 2025 Project NANDA. All Rights Reserved.
            </p>
            <p style={{ fontSize: '0.75rem', color: '#444', margin: 0 }}>
              Built by{' '}
              <a href="https://projectnanda.org" target="_blank" rel="noopener noreferrer"
                style={{ color: '#7a2020', textDecoration: 'none', fontWeight: '500' }}>
                Project NANDA
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
